import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { Alert, Button } from "@mapmycustomers/ui";

import cumulPromotionImage from "@app/assets/cumulPromotions/cumul-promotion.svg";
import DemoRequestModal from "@app/component/cumul/DemoRequestModal";
import { setCumulPromotionalUiVisited } from "@app/store/iam/actions";

import styles from "./PromotionalCumulDashboard.module.scss";

interface Props {
  onCumulPromotionalUiVisit: typeof setCumulPromotionalUiVisited.request;
}

const PromotionalCumulDashboard: React.FC<Props> = ({ onCumulPromotionalUiVisit }) => {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    onCumulPromotionalUiVisit();
  }, [onCumulPromotionalUiVisit]);

  return (
    <div className={styles.container}>
      <Row gutter={16}>
        <Col span={12}>
          <div className={styles.section}>
            <h1 className={styles.heading}>
              {intl.formatMessage({
                id: "reports.customDashboards.upgradeYourReports.heading",
                defaultMessage: "Custom Dashboard",
                description: "Heading of custom dashboard",
              })}
            </h1>
            <p className={styles.title}>
              {intl.formatMessage(
                {
                  id: "reports.customDashboards.upgradeYourReports.title",
                  defaultMessage:
                    "Uncover the sales activities that <strong>produce revenue</strong> ",
                  description: "Title of what cumul will do",
                },
                {
                  strong: (text: string) => <strong>{text}</strong>,
                }
              )}
            </p>
            <p className={styles.introduction}>
              {intl.formatMessage({
                id: "reports.customDashboards.upgradeYourReports.introduction",
                defaultMessage:
                  "Start connecting the dots between activity, rep performance, and revenue. With custom dashboards, you can report on any data you bring into MMC.",
                description: "Introduction to custom dashboards",
              })}
            </p>
            <ul className={styles.items}>
              <li>
                {intl.formatMessage(
                  {
                    id: "reports.customDashboards.upgradeYourReports.firstPoint",
                    defaultMessage:
                      "Make reports using your <strong>custom data</strong>, not just static charts",
                    description: "Custom dashboards first point",
                  },
                  {
                    strong: (text: string) => <strong>{text}</strong>,
                  }
                )}
              </li>
              <li>
                {intl.formatMessage(
                  {
                    id: "reports.customDashboards.upgradeYourReports.secondPoint",
                    defaultMessage: "Build <strong>scorecards</strong> to keep your reps on track ",
                    description: "Custom dashboards second point",
                  },
                  {
                    strong: (text: string) => <strong>{text}</strong>,
                  }
                )}
              </li>
              <li>
                {intl.formatMessage(
                  {
                    id: "reports.customDashboards.upgradeYourReports.thirdPoint",
                    defaultMessage:
                      "Get better sales insights with <strong>dashboards</strong> for regional teams and sales leadership",
                    description: "Custom dashboards third point",
                  },
                  {
                    strong: (text: string) => <strong>{text}</strong>,
                  }
                )}
              </li>
            </ul>

            <Alert
              icon={<FontAwesomeIcon className={styles.infoIcon} icon={faQuestionCircle} />}
              message={intl.formatMessage(
                {
                  id: "reports.customDashboards.upgradeYourReports.alertBox",
                  defaultMessage:
                    "Not sure you’ll know where to start? <strong>We’ve got you.</strong>{br}We’ll help you build and customize reports to your liking.",
                  description: "Alert box",
                },
                {
                  br: <br />,
                  strong: (text: string) => <strong>{text}</strong>,
                }
              )}
              showIcon
              type="info"
            />
            <div className={styles.buttonContainer}>
              <Button
                onClick={() => {
                  setVisible(true);
                }}
                size="large"
                type="primary"
              >
                {intl.formatMessage({
                  id: "reports.customDashboards.upgradeYourReports.getADemo",
                  defaultMessage: "Get a demo",
                  description: "Demo button",
                })}
              </Button>
            </div>
          </div>
        </Col>
        <Col span={12}>
          <img alt="" src={cumulPromotionImage} />
        </Col>
      </Row>
      <DemoRequestModal hideModal={setVisible} visible={visible} />
    </div>
  );
};

const mapDispatchToProps = {
  onCumulPromotionalUiVisit: setCumulPromotionalUiVisited.request,
};

export default connect(null, mapDispatchToProps)(PromotionalCumulDashboard);
